import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { ChartUserLoginTimes } from "@/components/chart-user-login-times";
import { CopyId } from "@/components/copy-id";
import { CreditLedger } from "@/components/credit-ledger";
import { CreditsDialog } from "@/components/credits-dialog";
import { usePermissions } from "@/hooks/use-permissions";
import { queryKeys } from "@/lib/queryKeys";
import { adminProvider } from "@/providers/admin.provider";
import { type UserBadgeGrant, badgesProvider } from "@/providers/badges.provider";
import { cardsProvider } from "@/providers/cards.provider";
import {
  type SendNotificationPayload,
  notificationsProvider,
} from "@/providers/notifications.provider";
import { type User, type Vehicle, usersProvider } from "@/providers/users.provider";
import {
  IconCar,
  IconChevronLeft,
  IconCoins,
  IconChevronRight,
  IconMedal,
  IconPhoto,
  IconSearch,
  IconSend,
  IconSparkles,
  IconUpload,
  IconUser,
  IconUsers,
  IconX,
} from "@tabler/icons-react";
import { formatImageInfo, useImageInfo } from "@/hooks/use-image-info";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { BadgeFilterCanvas } from "@/components/badge-filter-canvas";
import { UserRecommendationsTab } from "./user_recommendations";

const PAGE_SIZE = 50;

function ActiveCardImage({
  imgUrl,
  name,
  cardId,
  primaryColor,
  secondaryColor,
}: {
  imgUrl: string | null;
  name: string;
  cardId: string;
  primaryColor: string | null;
  secondaryColor: string | null;
}) {
  const info = useImageInfo(imgUrl);
  return (
    <div className="flex flex-col gap-1">
      {imgUrl ? (
        <>
          <img src={imgUrl} alt={name} className="w-full rounded-lg object-cover border" />
          {info && (
            <p className="text-xs text-muted-foreground tabular-nums px-0.5">
              {formatImageInfo(info)}
            </p>
          )}
        </>
      ) : (
        <div className="w-full h-48 rounded-lg bg-muted border flex items-center justify-center">
          <IconPhoto className="size-8 text-muted-foreground" />
        </div>
      )}
      <div className="flex items-center justify-between px-0.5">
        <span className="text-xs font-medium">{name}</span>
        <div className="flex items-center gap-1.5">
          {primaryColor && (
            <span
              className="size-3 rounded-full border shrink-0"
              style={{ backgroundColor: primaryColor }}
              title={`Primary: ${primaryColor}`}
            />
          )}
          {secondaryColor && (
            <span
              className="size-3 rounded-full border shrink-0"
              style={{ backgroundColor: secondaryColor }}
              title={`Secondary: ${secondaryColor}`}
            />
          )}
          <span className="text-[10px] text-green-600 font-medium">Active</span>
          <CopyId id={cardId} />
        </div>
      </div>
    </div>
  );
}

function UserRow({ user, onClick }: { user: User; onClick: () => void }) {
  const fullName =
    [user.first_name, user.last_name].filter(Boolean).join(" ") || "—";
  const createdAt = user.created_at
    ? new Date(user.created_at).toLocaleDateString()
    : "—";

  return (
    <TableRow className="cursor-pointer" onClick={onClick}>
      <TableCell>
        <CopyId id={user.user_id} />
      </TableCell>
      <TableCell className="font-medium">{user.username}</TableCell>
      <TableCell>{fullName}</TableCell>
      <TableCell className="text-muted-foreground text-xs">
        {createdAt}
      </TableCell>
    </TableRow>
  );
}

function BlockedUserRow({ userId }: { userId: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["user", userId],
    queryFn: () => usersProvider.getById(userId),
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-between py-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-48" />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between py-2 text-sm gap-4">
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="font-medium">
          {isError ? "Unknown user" : data?.username}
        </span>
      </div>
      <span className="font-mono text-xs text-muted-foreground shrink-0">
        {userId}
      </span>
    </div>
  );
}

function UserDetailSheet({
  user,
  onClose,
}: {
  user: User | null;
  onClose: () => void;
}) {
  const {
    data: blockedIds,
    isLoading: blocksLoading,
    isError: blocksError,
  } = useQuery({
    queryKey: ["user-blocks", user?.user_id],
    queryFn: () => usersProvider.getBlocks(user!.user_id),
    enabled: !!user,
  });

  const {
    data: userCards,
    isLoading: cardsLoading,
    isError: cardsError,
  } = useQuery({
    queryKey: ["user-cards", user?.user_id],
    queryFn: () => cardsProvider.getCardsForUser(user!.user_id),
    enabled: !!user,
  });

  const {
    data: userBadges,
    isLoading: badgesLoading,
    isError: badgesError,
  } = useQuery({
    queryKey: ["user-badges", user?.user_id],
    queryFn: () => badgesProvider.getForUser(user!.user_id),
    enabled: !!user,
  });

  const {
    data: detailUser,
    isLoading: detailLoading,
  } = useQuery({
    queryKey: ["user", user?.user_id],
    queryFn: () => usersProvider.getById(user!.user_id),
    enabled: !!user,
    staleTime: 60_000,
  });

  const { data: loginEvents, isLoading: loginTimeLoading } = useQuery({
    queryKey: queryKeys.userLoginHistoryAll(user?.user_id ?? ""),
    queryFn: () => adminProvider.getAllUserLoginHistory(user!.user_id),
    enabled: !!user,
    staleTime: 60_000,
  });

  // Bucket each login into a 5-minute slot of its own local_time, matching
  // the granularity of the server-aggregated heatmap, so the chart reflects
  // this user's clock instead of the server's.
  const loginTimeBuckets = useMemo(() => {
    const counts = new Map<string, number>();
    for (const event of loginEvents ?? []) {
      if (!event.local_time) continue;
      const [hh, mm] = event.local_time.split(":");
      const minute = Math.floor(Number(mm) / 5) * 5;
      const key = `${hh}:${String(minute).padStart(2, "0")}`;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return Array.from(counts, ([bucket, count]) => ({ bucket, count }));
  }, [loginEvents]);

  const fullName = user
    ? [user.first_name, user.last_name].filter(Boolean).join(" ") || "—"
    : "";
  const createdAt = user?.created_at
    ? new Date(user.created_at).toLocaleDateString()
    : "—";

  const queryClient = useQueryClient();
  const avatarFileRef = useRef<HTMLInputElement>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [creditsOpen, setCreditsOpen] = useState(false);
  // This sheet is gated by admin/users; credits are a separate area.
  const permissions = usePermissions();
  const canReadCredits = permissions.canRead("admin/credits");
  const canWriteCredits = permissions.canWrite("admin/credits");

  useEffect(() => {
    setAvatarPreview(null);
    setCreditsOpen(false);
  }, [user?.user_id]);

  const uploadAvatar = useMutation({
    mutationFn: async (file: File) => {
      const uploadResponse = await usersProvider.getAvatarUploadUrl(user!.user_id);
      await usersProvider.uploadAvatarToStorage(uploadResponse, file);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user", user!.user_id] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: () => setAvatarPreview(null),
  });

  function handleAvatarFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarPreview(URL.createObjectURL(file));
    uploadAvatar.mutate(file);
    e.target.value = "";
  }

  const [notifForm, setNotifForm] = useState<SendNotificationPayload>({
    action_type: "system_alert",
    body: "",
    redirect_path: "",
    actor_user_id: null,
    icon_obj_path: null,
  });
  const [notifSuccess, setNotifSuccess] = useState(false);

  const sendNotification = useMutation({
    mutationFn: (payload: SendNotificationPayload) =>
      notificationsProvider.sendToUser(user!.user_id, payload),
    onSuccess: () => {
      setNotifSuccess(true);
      setNotifForm({
        action_type: "system_alert",
        body: "",
        redirect_path: "",
        actor_user_id: null,
        icon_obj_path: null,
      });
      setTimeout(() => setNotifSuccess(false), 3000);
    },
  });

  return (
    <Sheet
      open={!!user}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent className="sm:max-w-md overflow-y-auto">
        {user && (
          <>
            <SheetHeader>
              <SheetTitle>{user.username}</SheetTitle>
              <SheetDescription className="font-mono text-xs">
                {user.user_id}
              </SheetDescription>
            </SheetHeader>

            <Tabs defaultValue="details" className="px-4">
              <TabsList className="w-full">
                <TabsTrigger value="details" className="flex-1">Details</TabsTrigger>
                <TabsTrigger value="activity" className="flex-1">Activity</TabsTrigger>
                <TabsTrigger value="notifications" className="flex-1">Notifications</TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="flex flex-col gap-4 mt-4">
                <div className="flex items-center gap-4">
                  <div
                    className="relative size-16 rounded-full border bg-muted flex items-center justify-center overflow-hidden shrink-0 cursor-pointer group"
                    onClick={() => avatarFileRef.current?.click()}
                  >
                    {avatarPreview ?? user.avatar_url ? (
                      <img
                        src={avatarPreview ?? user.avatar_url!}
                        alt={user.username}
                        className="size-full object-cover"
                      />
                    ) : (
                      <IconUser className="size-7 text-muted-foreground" />
                    )}
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-full">
                      <IconUpload className="size-4 text-white" />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 min-w-0">
                    <p className="text-sm font-medium truncate">{user.username}</p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-1.5 w-fit"
                      disabled={uploadAvatar.isPending}
                      onClick={() => avatarFileRef.current?.click()}
                    >
                      <IconUpload className="size-3.5" />
                      {uploadAvatar.isPending ? "Uploading…" : "Upload photo"}
                    </Button>
                    {uploadAvatar.isError && (
                      <p className="text-xs text-destructive">Upload failed</p>
                    )}
                  </div>
                  <input
                    ref={avatarFileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarFileChange}
                  />
                </div>

                <Separator />

                <div className="flex flex-col gap-3 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground shrink-0">Email</span>
                    <span className="truncate min-w-0">{user.email ?? "—"}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Name</span>
                    <span>{fullName}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Status</span>
                    <span>
                      {user.is_deleted
                        ? "Deleted"
                        : user.is_active === false
                          ? "Inactive"
                          : user.is_active === true
                            ? "Active"
                            : "—"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Joined</span>
                    <span>{createdAt}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Updated</span>
                    <span>
                      {user.updated_at
                        ? new Date(user.updated_at).toLocaleDateString()
                        : "—"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Date of birth</span>
                    <span>
                      {detailUser?.date_of_birth
                        ? new Date(detailUser.date_of_birth).toLocaleDateString()
                        : "—"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Locale</span>
                    <span className="font-mono text-xs">
                      {detailUser?.locale ?? (detailLoading ? "…" : "—")}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Timezone</span>
                    <span className="font-mono text-xs">
                      {user.timezone ?? "—"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">XP</span>
                    <span className="tabular-nums">
                      {(user.xp ?? 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Credits</span>
                    <span className="tabular-nums">
                      {(user.credits ?? 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Check-ins</span>
                    <span className="tabular-nums">
                      {detailUser != null ? (detailUser.check_in_count ?? 0).toLocaleString() : "—"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground shrink-0">Referral key</span>
                    <span className="font-mono text-xs truncate min-w-0">
                      {user.referral_key ?? "—"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground shrink-0">Referred by</span>
                    {user.referred_by ? (
                      <CopyId id={user.referred_by} />
                    ) : (
                      <span>—</span>
                    )}
                  </div>
                </div>

                <Separator />

                {canReadCredits && (
                  <>
                    <div className="flex flex-col gap-2">
                      <h3 className="font-semibold text-sm">Credits</h3>
                      {canWriteCredits && (
                        <>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="gap-1.5 w-fit"
                            onClick={() => setCreditsOpen(true)}
                          >
                            <IconCoins className="size-3.5" />
                            Adjust credits
                          </Button>
                          <p className="text-xs text-muted-foreground">
                            The user is notified only if the balance goes up.
                          </p>
                        </>
                      )}
                      <CreditLedger key={user.user_id} userId={user.user_id} />
                    </div>

                    <Separator />
                  </>
                )}

                <div className="flex flex-col gap-2">
                  <h3 className="font-semibold text-sm">
                    Blocked Users
                    {!blocksLoading && !blocksError && (
                      <span className="ml-2 text-muted-foreground font-normal">
                        ({blockedIds?.length ?? 0})
                      </span>
                    )}
                  </h3>

                  {blocksLoading ? (
                    <div className="flex flex-col gap-2 mt-1">
                      {Array.from({ length: 3 }).map((_, i) => (
                        <div
                          key={i}
                          className="flex items-center justify-between py-2"
                        >
                          <Skeleton className="h-4 w-24" />
                          <Skeleton className="h-4 w-48" />
                        </div>
                      ))}
                    </div>
                  ) : blocksError ? (
                    <p className="text-sm text-muted-foreground">
                      Failed to load blocks.
                    </p>
                  ) : !blockedIds?.length ? (
                    <p className="text-sm text-muted-foreground">
                      This user hasn't blocked anyone.
                    </p>
                  ) : (
                    <div className="flex flex-col divide-y">
                      {blockedIds.map((id) => (
                        <BlockedUserRow key={id} userId={id} />
                      ))}
                    </div>
                  )}
                </div>

                <Separator />

                <div className="flex flex-col gap-2">
                  <h3 className="font-semibold text-sm">
                    Profile Cards
                    {!cardsLoading && !cardsError && (
                      <span className="ml-2 text-muted-foreground font-normal">
                        ({userCards?.length ?? 0})
                      </span>
                    )}
                  </h3>

                  {cardsLoading ? (
                    <div className="flex flex-col gap-2 mt-1">
                      <Skeleton className="h-48 w-full rounded-lg" />
                      {Array.from({ length: 2 }).map((_, i) => (
                        <Skeleton key={i} className="h-8 w-full" />
                      ))}
                    </div>
                  ) : cardsError ? (
                    <p className="text-sm text-muted-foreground">
                      Failed to load cards.
                    </p>
                  ) : !userCards?.length ? (
                    <p className="text-sm text-muted-foreground">
                      This user has no cards.
                    </p>
                  ) : (() => {
                    const activeCard = user.active_card_id
                      ? userCards.find((c) => c.profile_card_id === user.active_card_id)
                      : null;
                    const otherCards = userCards.filter(
                      (c) => c.profile_card_id !== user.active_card_id
                    );
                    return (
                      <div className="flex flex-col gap-2">
                        {user.active_card_id && (
                          <ActiveCardImage
                            imgUrl={user.active_card_url}
                            name={activeCard?.name ?? "Active card"}
                            cardId={user.active_card_id}
                            primaryColor={user.active_card_primary_color}
                            secondaryColor={user.active_card_secondary_color}
                          />
                        )}
                        {otherCards.length > 0 && (
                          <div className="flex flex-col divide-y rounded-md border text-xs">
                            {otherCards.map((card) => (
                              <div
                                key={card.profile_card_id}
                                className="flex items-center gap-2 px-2 py-1.5"
                              >
                                {card.img_url ? (
                                  <img
                                    src={card.img_url}
                                    alt={card.name}
                                    className="size-5 rounded object-cover shrink-0"
                                  />
                                ) : (
                                  <div className="size-5 rounded bg-muted border flex items-center justify-center shrink-0">
                                    <IconPhoto className="size-3 text-muted-foreground" />
                                  </div>
                                )}
                                <span className="flex-1">{card.name}</span>
                                <CopyId id={card.profile_card_id} />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>

                <Separator />

                <div className="flex flex-col gap-2">
                  <h3 className="font-semibold text-sm">
                    Vehicles
                    {!detailLoading && (
                      <span className="ml-2 text-muted-foreground font-normal">
                        ({detailUser?.vehicles?.length ?? 0})
                      </span>
                    )}
                  </h3>

                  {detailLoading ? (
                    <div className="flex flex-col gap-2 mt-1">
                      {Array.from({ length: 2 }).map((_, i) => (
                        <Skeleton key={i} className="h-10 w-full" />
                      ))}
                    </div>
                  ) : !detailUser?.vehicles?.length ? (
                    <p className="text-sm text-muted-foreground">
                      This user has no registered vehicles.
                    </p>
                  ) : (
                    <div className="flex flex-col divide-y rounded-md border text-xs">
                      {detailUser.vehicles.map((v: Vehicle) => (
                        <div key={v.vehicle_id} className="flex items-center gap-2 px-2 py-2">
                          {v.photo_url ? (
                            <img
                              src={v.photo_url}
                              alt={`${v.year} ${v.make} ${v.model}`}
                              className="size-10 rounded object-cover shrink-0 border"
                            />
                          ) : (
                            <div className="size-10 rounded bg-muted border flex items-center justify-center shrink-0">
                              <IconCar className="size-4 text-muted-foreground" />
                            </div>
                          )}
                          <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                            <span className="font-medium truncate">
                              {v.year} {v.make} {v.model}
                              {v.nickname ? ` — ${v.nickname}` : ""}
                            </span>
                            <span className="text-muted-foreground truncate">
                              {[
                                v.vehicle_type,
                                v.drive,
                                v.transmission ? (v.transmission === "M" ? "Manual" : v.transmission === "A" ? "Auto" : v.transmission) : null,
                                v.horsepower ? `${v.horsepower} hp` : null,
                              ].filter(Boolean).join(" · ")}
                            </span>
                          </div>
                          <CopyId id={v.vehicle_id} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Separator />

                <div className="flex flex-col gap-2">
                  <h3 className="font-semibold text-sm">
                    Badges
                    {!badgesLoading && !badgesError && (
                      <span className="ml-2 text-muted-foreground font-normal">
                        ({userBadges?.length ?? 0})
                      </span>
                    )}
                  </h3>

                  {badgesLoading ? (
                    <div className="flex flex-col gap-2 mt-1">
                      {Array.from({ length: 3 }).map((_, i) => (
                        <Skeleton key={i} className="h-8 w-full" />
                      ))}
                    </div>
                  ) : badgesError ? (
                    <p className="text-sm text-muted-foreground">
                      Failed to load badges.
                    </p>
                  ) : !userBadges?.length ? (
                    <p className="text-sm text-muted-foreground">
                      This user has no badges.
                    </p>
                  ) : (
                    <div className="flex flex-col divide-y rounded-md border text-xs">
                      {userBadges.map((badge: UserBadgeGrant) => (
                        <div
                          key={`${badge.variant_id}-${badge.granted_at}`}
                          className="flex items-center gap-2 px-2 py-1.5"
                        >
                          <div className="shrink-0">
                            {badge.filter_img_url ? (
                              <BadgeFilterCanvas
                                baseImageUrl={badge.img_url}
                                filterImageUrl={badge.filter_img_url}
                                opacity={0.5}
                                size={24}
                              />
                            ) : badge.img_url ? (
                              <img
                                src={badge.img_url}
                                alt={badge.name}
                                className="size-6 rounded object-cover border"
                              />
                            ) : (
                              <div className="size-6 rounded bg-muted border flex items-center justify-center">
                                <IconMedal className="size-3.5 text-muted-foreground" />
                              </div>
                            )}
                          </div>
                          <span className="flex-1">{badge.name}</span>
                          {badge.granted_at && (
                            <span className="text-[10px] text-muted-foreground">
                              {new Date(badge.granted_at).toLocaleDateString()}
                            </span>
                          )}
                          <span className="font-mono text-[10px] bg-muted px-1.5 py-0.5 rounded text-muted-foreground">
                            {badge.variant_name}
                          </span>
                          <CopyId id={badge.badge_id} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="activity" className="flex flex-col gap-4 mt-4">
                <ChartUserLoginTimes
                  data={loginTimeBuckets}
                  isLoading={loginTimeLoading}
                  timeLabel="user time"
                />
              </TabsContent>

              <TabsContent value="notifications" className="flex flex-col gap-4 mt-4">
                <p className="text-sm text-muted-foreground">
                  Send a push notification directly to this user.
                </p>
                <form
                  className="flex flex-col gap-3"
                  onSubmit={(e) => {
                    e.preventDefault();
                    sendNotification.mutate(notifForm);
                  }}
                >
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="action_type">Action type</Label>
                    <Input
                      id="action_type"
                      value={notifForm.action_type}
                      onChange={(e) =>
                        setNotifForm((f) => ({ ...f, action_type: e.target.value }))
                      }
                      placeholder="system_alert"
                      required
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="notif_body">Body</Label>
                    <Textarea
                      id="notif_body"
                      value={notifForm.body}
                      onChange={(e) =>
                        setNotifForm((f) => ({ ...f, body: e.target.value }))
                      }
                      placeholder="Your account has been reviewed."
                      rows={3}
                      required
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="notif_icon_obj_path">
                      Icon object path{" "}
                      <span className="text-muted-foreground font-normal">(optional)</span>
                    </Label>
                    <Input
                      id="notif_icon_obj_path"
                      value={notifForm.icon_obj_path ?? ""}
                      onChange={(e) =>
                        setNotifForm((f) => ({ ...f, icon_obj_path: e.target.value || null }))
                      }
                      placeholder="avatars/user_123.png"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="redirect_path">Redirect path</Label>
                    <Input
                      id="redirect_path"
                      value={notifForm.redirect_path}
                      onChange={(e) =>
                        setNotifForm((f) => ({ ...f, redirect_path: e.target.value }))
                      }
                      placeholder="/profile"
                      required
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="actor_user_id">
                      Actor user ID{" "}
                      <span className="text-muted-foreground font-normal">(optional)</span>
                    </Label>
                    <Input
                      id="actor_user_id"
                      value={notifForm.actor_user_id ?? ""}
                      onChange={(e) =>
                        setNotifForm((f) => ({
                          ...f,
                          actor_user_id: e.target.value || null,
                        }))
                      }
                      placeholder="Leave blank for system"
                    />
                  </div>

                  {sendNotification.isError && (
                    <p className="text-sm text-destructive">
                      {(sendNotification.error as Error).message}
                    </p>
                  )}
                  {notifSuccess && (
                    <p className="text-sm text-green-600">Notification sent.</p>
                  )}

                  <Button
                    type="submit"
                    disabled={sendNotification.isPending}
                    className="gap-2"
                  >
                    <IconSend className="size-4" />
                    {sendNotification.isPending ? "Sending…" : "Send notification"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>

            {canWriteCredits && (
              <CreditsDialog
                open={creditsOpen}
                onOpenChange={setCreditsOpen}
                target={{
                  kind: "user",
                  id: user.user_id,
                  name: user.username,
                }}
              />
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

export function UsersPage() {
  const [page, setPage] = useState(1);
  const [searchPage, setSearchPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setSearchPage(1);
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  const isSearching = debouncedSearch.length >= 1;

  const {
    data: result,
    isLoading,
    isError,
  } = useQuery({
    queryKey: isSearching
      ? ["users-search", debouncedSearch, searchPage]
      : ["users", page],
    queryFn: async () => {
      if (isSearching) {
        const data = await usersProvider.search(debouncedSearch, searchPage);
        return { data, hasNext: data.length === PAGE_SIZE };
      }
      const res = await usersProvider.list(page);
      return { data: res.data, hasNext: res.has_next };
    },
    staleTime: isSearching ? 30_000 : undefined,
  });

  const users = result?.data;
  const hasMore = result?.hasNext ?? false;

  function handleClearSearch() {
    setSearchInput("");
    setDebouncedSearch("");
    setSearchPage(1);
  }

  return (
    <div className="flex flex-col h-full">
      <SiteHeader title="Users" />

      <Tabs defaultValue="users" className="flex flex-col flex-1 min-h-0">
        <div className="flex items-center px-4 pt-4 pb-2 lg:px-6 lg:pt-6">
          <TabsList>
            <TabsTrigger value="users" className="gap-1.5">
              <IconUsers className="size-4" />
              Users
            </TabsTrigger>
            <TabsTrigger value="recommendations" className="gap-1.5">
              <IconSparkles className="size-4" />
              Recommendations
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent
          value="recommendations"
          className="flex-1 overflow-auto px-4 pb-4 lg:px-6 lg:pb-6"
        >
          <UserRecommendationsTab />
        </TabsContent>

        <TabsContent
          value="users"
          className="flex flex-col gap-4 flex-1 px-4 pb-4 lg:px-6 lg:pb-6 min-h-0"
        >
          {/* Toolbar */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="relative w-full sm:w-64">
              <IconSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Search by username, email or id…"
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  setPage(1);
                }}
                className="pl-8 pr-8"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <IconX className="size-4" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <IconUsers className="size-4" />
              <span className="text-sm">
                {isLoading
                  ? "Loading…"
                  : users
                    ? isSearching
                      ? `${users.length} result${users.length !== 1 ? "s" : ""} on page ${searchPage}`
                      : `${users.length} user${users.length !== 1 ? "s" : ""} on page ${page}`
                    : ""}
              </span>
            </div>
          </div>

          {/* Table */}
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User ID</TableHead>
                  <TableHead>Username</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Joined</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 10 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 4 }).map((_, j) => (
                        <TableCell key={j}>
                          <Skeleton className="h-4 w-full" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : isError ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="py-12 text-center text-muted-foreground"
                    >
                      Failed to load users. Check your connection and try again.
                    </TableCell>
                  </TableRow>
                ) : !users?.length ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="py-12 text-center text-muted-foreground"
                    >
                      {page > 1
                        ? "No more users on this page."
                        : "No users found."}
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <UserRow
                      key={user.user_id}
                      user={user}
                      onClick={() => setSelectedUser(user)}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {(() => {
            const activePage = isSearching ? searchPage : page;
            const setActivePage = isSearching ? setSearchPage : setPage;
            return activePage > 1 || hasMore ? (
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={activePage === 1}
                  onClick={() => setActivePage((p) => p - 1)}
                  className="gap-1"
                >
                  <IconChevronLeft className="size-3.5" />
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {activePage}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!hasMore}
                  onClick={() => setActivePage((p) => p + 1)}
                  className="gap-1"
                >
                  Next
                  <IconChevronRight className="size-3.5" />
                </Button>
              </div>
            ) : null;
          })()}
        </TabsContent>
      </Tabs>

      <UserDetailSheet
        user={selectedUser}
        onClose={() => setSelectedUser(null)}
      />
    </div>
  );
}
